import { useState } from "react";
import { Hammer, Paintbrush, Layers, LayoutGrid, ArrowUpDown, Ruler, PlusCircle, Phone, MessageSquare } from "lucide-react";
import { motion } from "framer-motion";
import ScrollReveal from "./ScrollReveal";
import MagneticButton from "./MagneticButton";

// Gallery images for each service - optimized selections
import hardwoodImg from "@/assets/gallery/hardwood-install-5.jpg"; // Large open floor plan - shows finished installation
import sandFinishImg from "@/assets/gallery/sand-finish-11.png"; // Before/after comparison - shows transformation
import lvpImg from "@/assets/gallery/lvp-install-2.jpg"; // Wide plank sunlit room - impressive result
import laminateImg from "@/assets/gallery/lvp-install-6.jpg"; // Vaulted ceiling room - dramatic shot
import stairsImg from "@/assets/gallery/stairs-3.jpg"; // Custom bullnose treads - shows detail work

const services = [
  {
    icon: Paintbrush,
    title: "Hardwood Floor Refinishing",
    description: "Restore worn or damaged hardwood with a full sand, stain, and finish process. We focus on even sanding, consistent color, and durable water-based finishes that bring life back to your floors.",
    image: sandFinishImg,
  },
  {
    icon: Hammer,
    title: "Hardwood Floor Installation",
    description: "Professional installation of solid and engineered hardwood flooring. Proper layout, clean cuts, and solid fastening to ensure long-term performance and a clean final look.",
    image: hardwoodImg,
  },
  {
    icon: Layers,
    title: "LVP Installation",
    description: "Durable, waterproof flooring installed the right way. We handle subfloor prep, layout, and clean transitions so your floors look good and perform as intended.",
    image: lvpImg,
  },
  {
    icon: LayoutGrid,
    title: "Laminate Installation",
    description: "Modern laminate flooring installed with professional precision. Proper acclimation, careful layout, and clean finishes throughout.",
    image: laminateImg,
  },
  {
    icon: ArrowUpDown,
    title: "Stairs: Treads, Risers & More",
    description: "Complete stair work including treads, risers, stringers, and end caps. Clean transitions that match your floors and hold up to daily traffic.",
    image: stairsImg,
  },
  {
    icon: Ruler,
    title: "Baseboard Removal & Reinstall",
    description: "Careful removal and reinstallation of existing baseboards during your floor installation project.",
    image: null,
  },
  {
    icon: PlusCircle,
    title: "New Baseboard Installation",
    description: "Fresh baseboard installation to complete your flooring project with a clean, finished look.",
    image: null,
  },
];

const ServiceCard = ({ service, index }: { service: typeof services[0]; index: number }) => {
  const [isFlipped, setIsFlipped] = useState(false);
  const hasImage = service.image !== null;

  if (!hasImage) {
    // Non-flippable card for services without images
    return (
      <ScrollReveal delay={index * 0.1}>
        <div className="card-service flex flex-col items-start h-72">
          <div className="w-14 h-14 bg-accent/10 rounded-lg flex items-center justify-center mb-4">
            <service.icon className="w-7 h-7 text-accent" />
          </div>
          <h3 className="font-heading font-bold text-lg mb-2">{service.title}</h3>
          <p className="text-muted-foreground text-sm leading-relaxed">{service.description}</p>
        </div>
      </ScrollReveal>
    );
  }

  return (
    <ScrollReveal delay={index * 0.1}>
      <div
        className="relative h-72 cursor-pointer perspective-1000"
        onClick={() => setIsFlipped(!isFlipped)}
      >
        <motion.div
          className="relative w-full h-full"
          initial={false}
          animate={{ rotateY: isFlipped ? 180 : 0 }}
          transition={{ duration: 0.6, ease: "easeInOut" }}
          style={{ transformStyle: "preserve-3d" }}
        >
          {/* Front Side */}
          <div
            className="absolute inset-0 card-service flex flex-col items-start backface-hidden"
            style={{ backfaceVisibility: "hidden" }}
          >
            <div className="w-14 h-14 bg-accent/10 rounded-lg flex items-center justify-center mb-4">
              <service.icon className="w-7 h-7 text-accent" />
            </div>
            <h3 className="font-heading font-bold text-lg mb-2">{service.title}</h3>
            <p className="text-muted-foreground text-sm leading-relaxed">{service.description}</p>
            <span className="mt-auto text-xs text-accent font-medium pt-3">Click to see our work →</span>
          </div>

          {/* Back Side */}
          <div
            className="absolute inset-0 rounded-xl overflow-hidden backface-hidden"
            style={{ backfaceVisibility: "hidden", transform: "rotateY(180deg)" }}
          >
            <img
              src={service.image}
              alt={service.title}
              className="w-full h-full object-cover"
              loading="lazy"
              width={400}
              height={288}
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
            <div className="absolute bottom-0 left-0 right-0 p-4">
              <h3 className="font-heading font-bold text-lg text-white">{service.title}</h3>
              <span className="text-xs text-white/80">Click to flip back</span>
            </div>
          </div>
        </motion.div>
      </div>
    </ScrollReveal>
  );
};

const ServicesSection = () => {
  return (
    <section className="py-20 bg-muted">
      <div className="container mx-auto px-4">
        <ScrollReveal>
          <div className="text-center mb-12">
            <h2 className="section-heading">Our Flooring Services</h2>
            <p className="section-subheading mx-auto">
              We specialize in quality flooring work for homeowners who want clean installs, proper prep, and results that hold up over time. From refinishing existing hardwood to new installations, every job is handled with care and attention to detail.
            </p>
          </div>
        </ScrollReveal>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
          {services.map((service, index) => (
            <ServiceCard key={index} service={service} index={index} />
          ))}
        </div>

        {/* Trust Line */}
        <ScrollReveal>
          <div className="text-center mb-10">
            <p className="text-muted-foreground font-heading tracking-wide">
              Locally owned • Clean, professional work • Free estimates available
            </p>
          </div>
        </ScrollReveal>

        {/* CTA */}
        <ScrollReveal delay={0.2}>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <MagneticButton>
              <a
                href="tel:+17192327424"
                className="btn-cta-primary flex items-center justify-center gap-3"
              >
                <Phone size={20} />
                Call for Free Estimate
              </a>
            </MagneticButton>
            <MagneticButton>
              <a
                href="sms:+17192327424"
                className="btn-cta-secondary flex items-center justify-center gap-3"
              >
                <MessageSquare size={20} />
                Text Us
              </a>
            </MagneticButton>
          </div>
        </ScrollReveal>
      </div>
    </section>
  );
};

export default ServicesSection;
